import { Box, Divider, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import { ActionLink, CurrencyFormatters } from "halifax";
import React, { useContext, useMemo } from "react";
import {
  CarReservation,
  MyTripsFilter,
  PaymentLineItemEnum,
  PaymentLineItemTravelWallet,
  PaymentLineItemTravelWalletEnum,
  PaymentTypeEnum,
} from "redmond";

import * as textConstants from "./constants";
import "./styles.scss";
import { ClientContext } from "../../../../../../../../App";
import { usePrintItemizedReceipt } from "../../usePrintItemizedReceipt";

interface ICarItemizedReceiptModalContentProps {
  car: CarReservation;
  documentId?: string;
  isMobile?: boolean;
  tripsFilter: MyTripsFilter;
}

export const CarItemizedReceiptModalContent = ({
  car,
  documentId = textConstants.DEFAULT_DOC_ID,
  isMobile,
  tripsFilter,
}: ICarItemizedReceiptModalContentProps) => {
  const { bookResult, paymentBreakdown, paymentAmountInfo } = car;
  const { rentalRate, vehicle, driver, pickUp, dropOff, confirmationInfo, groundBookingId } =
    bookResult;
  const { totalCharge, feesSummary, payAtDesk, base } = rentalRate;
  const { logo } = useContext(ClientContext);

  const breakdownPayments =
    paymentBreakdown?.payments as PaymentLineItemTravelWallet[];
  const walletItemPayments = breakdownPayments.filter(
    (p) => p.PaymentLineItem === PaymentLineItemEnum.TravelWallet
  );

  const confirmationCode = `H-${
    confirmationInfo?.supplierBookingId ||
    confirmationInfo?.providerBookingId ||
    groundBookingId
  }`;

  const driverName = `${driver.givenName} ${driver.surname}`;
  const currencyCode = totalCharge.fiat.currencyCode;
  const addCurrency = (num: number) =>
    `${CurrencyFormatters.get(currencyCode).format(Math.abs(num))}`;

  const effectiveTotal =
    totalCharge.fiat.value -
    walletItemPayments.reduce((acc, p) => acc + p.amount.amount, 0);

  const paymentInfo = {
    numberDisplay: "",
    rewardsDisplay: "",
    creditApplied: breakdownPayments.some(
      (p) =>
        p.TravelWallet === PaymentLineItemTravelWalletEnum.TravelWalletCredit
    ),
    offerApplied: breakdownPayments.some(
      (p) =>
        p.TravelWallet === PaymentLineItemTravelWalletEnum.TravelWalletOffer
    ),
  };

  if (paymentAmountInfo?.PaymentAmountInfo === PaymentTypeEnum.FiatAmountInfo) {
    paymentInfo.numberDisplay = paymentAmountInfo.numberDisplay;
  } else if (
    paymentAmountInfo?.PaymentAmountInfo === PaymentTypeEnum.RewardsAmountInfo
  ) {
    paymentInfo.numberDisplay = paymentAmountInfo.accountDisplayName;
  } else if (
    paymentAmountInfo?.PaymentAmountInfo === PaymentTypeEnum.SplitAmountInfo
  ) {
    paymentInfo.numberDisplay = paymentAmountInfo.fiatInfo.numberDisplay;
    paymentInfo.rewardsDisplay =
      paymentAmountInfo.rewardsInfo.accountDisplayName;
  }

  const { printing, handlePrint } = usePrintItemizedReceipt({
    documentId,
    tripId: groundBookingId,
    travelType: "car",
    tripsFilter,
    printWindowTitle: `${driverName} - ${confirmationCode} ${textConstants.TITLE}`,
  });

  const renderPrintButton = useMemo(() => {
    if (printing || isMobile) return null;
    return (
      <ActionLink
        className="print-btn"
        content={textConstants.PRINT}
        onClick={handlePrint}
      />
    );
  }, [isMobile, printing]);

  return (
    <Box className="itemized-receipt-modal-container" id={documentId}>
      <Box className="itemized-receipt-modal-content">
        <Box className="itemized-receipt-logo-container">
          {logo}
          {renderPrintButton}
        </Box>
        <Box className="itemized-receipt-title-container">
          <Typography variant="h2">{textConstants.TITLE}</Typography>
        </Box>
        <Box className="itemized-receipt-details">
          <Box className="line-item">
            <Typography variant="body1">{textConstants.DRIVER_NAME}:</Typography>
            <Typography variant="body1" className="bold">
              {driverName}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1">
              {textConstants.CONFIRMATION_NUMBER}:
            </Typography>
            <Typography variant="body1" className="bold">
              {confirmationCode}
            </Typography>
          </Box>
          {vehicle.name && (
            <Box className="line-item">
              <Typography variant="body1">{textConstants.VEHICLE}:</Typography>
              <Typography variant="body1" className="bold">
                {vehicle.name}
              </Typography>
            </Box>
          )}
          <Box className="line-item">
            <Typography variant="body1">{textConstants.PICK_UP}:</Typography>
            <Typography variant="body1" className="bold">
              {dayjs(pickUp.dateTime).format(textConstants.MMDDYYYY_FORMAT)}
            </Typography>
          </Box>
          <Box className="line-item gutter-bottom">
            <Typography variant="body1">{textConstants.DROP_OFF}:</Typography>
            <Typography variant="body1" className="bold">
              {dayjs(dropOff.dateTime).format(textConstants.MMDDYYYY_FORMAT)}
            </Typography>
          </Box>
          <Divider className="line-item gutter-bottom" />
          {base && (
            <Box className="line-item">
              <Typography variant="body1" className="bold">
                {textConstants.BASE_FARE}
              </Typography>
              <Typography variant="body1" className="bold">
                {addCurrency(base.value)}
              </Typography>
            </Box>
          )}
          {feesSummary?.total && (
            <Box className="line-item">
              <Typography variant="body1" className="bold">
                {textConstants.TAXES_AND_FEES}
              </Typography>
              <Typography variant="body1" className="bold">
                {addCurrency(feesSummary.total.value)}
              </Typography>
            </Box>
          )}
          {walletItemPayments.map((breakdownPayment) =>
            "breakdown" in breakdownPayment &&
            breakdownPayment.breakdown?.length ? (
              breakdownPayment.breakdown.map((item) => (
                <Box key={item.description} className="line-item positive">
                  <Typography variant="body1" className="bold">
                    {item.description ||
                      textConstants.TRAVEL_CREDITS_LINE_ITEM_APPLIED}
                  </Typography>
                  <Typography variant="body1" className="bold">
                    -{addCurrency(Math.abs(item.amount.amount))}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box className="line-item positive">
                <Typography variant="body1" className="bold">
                  {breakdownPayment.TravelWallet ===
                  PaymentLineItemTravelWalletEnum.TravelWalletCredit
                    ? textConstants.TRAVEL_CREDITS_LINE_ITEM_APPLIED
                    : textConstants.TRAVEL_OFFER_LINE_ITEM_APPLIED}
                </Typography>
                <Typography variant="body1" className="bold">
                  -{addCurrency(Math.abs(breakdownPayment.amount.amount))}
                </Typography>
              </Box>
            )
          )}
          <Box className={payAtDesk ? "line-item" : "line-item gutter-bottom"}>
            <Typography variant="body1" className="bold">
              {textConstants.TOTAL_COST}
            </Typography>
            <Typography variant="body1" className="bold">
              {addCurrency(effectiveTotal)}
            </Typography>
          </Box>
          {payAtDesk && (
            <Box className="line-item gutter-bottom">
              <Typography variant="body1" className="bold">
                {textConstants.DUE_AT_PICKUP}
              </Typography>
              <Typography variant="body1" className="bold">
                {addCurrency(payAtDesk.value)}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" className="line-item gutter-bottom">
            {textConstants.COPY1}
          </Typography>
          {((paymentInfo.numberDisplay &&
            !paymentInfo.numberDisplay?.includes("Unavailable")) ||
            paymentInfo.rewardsDisplay ||
            paymentInfo.creditApplied ||
            paymentInfo.offerApplied) && (
            <Box>
              <Typography variant="body1" className="gutter-bottom bold">
                {textConstants.PAYMENT_INFO}
              </Typography>
              {paymentInfo.numberDisplay && (
                <Typography variant="body1" className="gutter-bottom">
                  {`...${paymentInfo.numberDisplay.slice(-4)}`}
                </Typography>
              )}
              {paymentInfo.rewardsDisplay && (
                <Typography variant="body1" className="gutter-bottom">
                  {`...${paymentInfo.rewardsDisplay.slice(-4)}`}
                </Typography>
              )}
              {paymentInfo.creditApplied &&
                !paymentInfo.rewardsDisplay &&
                !paymentInfo.numberDisplay && (
                  <Typography variant="body1" className="gutter-bottom">
                    {textConstants.TRAVEL_CREDIT_APPLIED}
                  </Typography>
                )}
              {paymentInfo.offerApplied &&
                !paymentInfo.rewardsDisplay &&
                !paymentInfo.numberDisplay && (
                  <Typography variant="body1" className="gutter-bottom">
                    {textConstants.TRAVEL_OFFER_APPLIED}
                  </Typography>
                )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
